import { FC, useState, useCallback } from 'react';
import { InvestorProfile } from '@/types/index';
import { investorProfileService } from '@services/investor-profile.service';
import { WizardProgress } from './WizardProgress';
import { Step1Objectifs } from './steps/Step1Objectifs';
import { Step2Strategie } from './steps/Step2Strategie';
import { Step3MoyensFinanciers } from './steps/Step3MoyensFinanciers';
import { Step5CriteresFinanciers } from './steps/Step5CriteresFinanciers';
import { Step6Notifications } from './steps/Step6Notifications';
import { Step7Resume } from './steps/Step7Resume';

interface Props {
  initialProfile: InvestorProfile;
  onSaved?: (profile: InvestorProfile) => void;
}

const STEP_TITLES = [
  'Vos objectifs',
  'Votre stratégie',
  'Vos moyens financiers',
  'Critères de rejet automatique',
  'Notifications',
  'Résumé de votre profil',
];

export const InvestorProfileWizard: FC<Props> = ({ initialProfile, onSaved: _onSaved }) => {
  const [profile, setProfile] = useState<InvestorProfile>(initialProfile);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedStep, setCompletedStep] = useState(Math.min(initialProfile.wizardStep ?? 0, 5));
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedOk, setSavedOk] = useState(false);

  const handleChange = useCallback((data: Partial<InvestorProfile>) => {
    setProfile(prev => ({ ...prev, ...data }));
    setSavedOk(false);
  }, []);

  const saveCurrentStep = async (nextStep: number) => {
    setIsSaving(true);
    setSaveError(null);
    try {
      // Exclure les champs lecture seule rejetés par le DTO backend
      const { id: _id, userId: _userId, createdAt: _createdAt, updatedAt: _updatedAt, ...updateData } = profile;
      const updated = await investorProfileService.update({
        ...updateData,
        wizardStep: Math.max(completedStep, currentStep),
      });
      setProfile(updated);
      setCompletedStep(Math.max(completedStep, currentStep));
      setSavedOk(true);
      setCurrentStep(nextStep);
      setTimeout(() => setSavedOk(false), 2000);
    } catch {
      setSaveError('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const goNext = () => {
    if (currentStep < 5) saveCurrentStep(currentStep + 1);
  };

  const goPrev = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleStepClick = (step: number) => {
    if (step !== currentStep) {
      if (step > currentStep) {
        saveCurrentStep(step);
      } else {
        setCurrentStep(step);
      }
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0: return <Step1Objectifs profile={profile} onChange={handleChange} />;
      case 1: return <Step2Strategie profile={profile} onChange={handleChange} />;
      case 2: return <Step3MoyensFinanciers profile={profile} onChange={handleChange} />;
      case 3: return <Step5CriteresFinanciers profile={profile} onChange={handleChange} />;
      case 4: return <Step6Notifications profile={profile} onChange={handleChange} />;
      case 5: return <Step7Resume profile={profile} />;
      default: return null;
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <WizardProgress
        currentStep={currentStep}
        completedStep={completedStep}
        onStepClick={handleStepClick}
      />

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {STEP_TITLES[currentStep]}
          </h2>
          <span className="text-sm text-gray-400">
            {currentStep + 1} / 6
          </span>
        </div>

        {renderStep()}

        {saveError && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400">{saveError}</p>
        )}

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={goPrev}
            disabled={currentStep === 0}
            className="px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Précédent
          </button>

          {savedOk && (
            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
              ✓ Sauvegardé
            </span>
          )}

          {currentStep < 5 ? (
            <button
              onClick={goNext}
              disabled={isSaving}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-xl text-sm font-semibold transition-colors flex items-center gap-2"
            >
              {isSaving ? (
                <><span className="animate-spin inline-block">↻</span> Sauvegarde…</>
              ) : (
                'Suivant →'
              )}
            </button>
          ) : (
            <button
              onClick={() => saveCurrentStep(5)}
              disabled={isSaving}
              className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white rounded-xl text-sm font-semibold transition-colors"
            >
              {isSaving ? 'Sauvegarde…' : 'Sauvegarder le profil'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
